import axios, { AxiosResponse } from 'axios';

const BASE_URL = window.location.hostname === 'localhost' ? 'https://localhost:7000/api' : 'https://dealden.ca/api';

// CSRF token cache for admin API
let adminCsrfToken: string | null = null;

// Function to get CSRF token for admin API
const getAdminCsrfToken = async (): Promise<string | null> => {
  if (adminCsrfToken) {
    return adminCsrfToken;
  }
  
  try {
    const response = await axios.get(`${BASE_URL}/auth/csrf-token`, {
      withCredentials: true
    });
    adminCsrfToken = response.data.token;
    return adminCsrfToken;
  } catch (error) {
    console.error('Failed to get CSRF token:', error);
    return null;
  }
};



// Create axios instance for admin API
const adminApiClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // Include cookies for authentication
  headers: {
    'Content-Type': 'application/json',
  },
});

// Export the admin API client for use in other files
export { adminApiClient };

// Add request interceptor to ensure cookies are sent
adminApiClient.interceptors.request.use(
  async (config) => {
    // Ensure credentials are always sent with admin requests
    config.withCredentials = true;
    
    // Add CSRF token for state-changing requests
    if (config.method && ['post', 'put', 'delete', 'patch'].includes(config.method.toLowerCase())) {
      const token = await getAdminCsrfToken();
      if (token) {
        config.headers['X-CSRF-Token'] = token;
      }
    }
    
    // Debug logging in development
    if (process.env.NODE_ENV === 'development') {
      console.log('🔵 Admin API Request:', config.method?.toUpperCase(), config.url);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
adminApiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Debug logging in development
    if (process.env.NODE_ENV === 'development') {
      console.group('🔴 Admin API Error');
      console.error('Request URL:', error.config?.url);
      console.error('Status:', error.response?.status);
      console.error('Message:', error.response?.data?.message || error.message);
      console.error('Cookies:', document.cookie);
      console.groupEnd();
    }

    if (error.response?.status === 401) {
      // Only redirect to admin login if we're on admin pages but not already on login page
      const currentPath = window.location.pathname;
      const adminAuthEndpoints = ['/admin/login'];
      const isAdminAuthEndpoint = adminAuthEndpoints.some(endpoint => error.config?.url?.includes(endpoint));
      
      if (currentPath.startsWith('/admin') && !currentPath.includes('/login') && !isAdminAuthEndpoint) {
        console.warn('Admin unauthorized access detected, redirecting to admin login');
        window.location.replace('/admin/login');
      }
    }
    return Promise.reject(error);
  }
);

// Admin types
export interface AdminLoginRequest {
  username: string;
  password: string;
}

export interface AdminUser {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
  createdDate: string;
  lastLoginDate?: string;
  profileImageUrl?: string;
}

export interface AdminLoginResponse {
  message: string;
  admin: AdminUser;
  token: string;
}

export interface CreateAdminRequest {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  role: number; // 0=SuperAdmin, 1=Admin, 2=Moderator, 3=Support
}

export interface AdminDetailDto {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[]; // Support multiple roles
  isActive: boolean;
  createdDate: string;
  lastLoginDate?: string;
  profileImageUrl?: string;
  loginAttempts: number;
  lastPasswordChange?: string;
  createdBy?: string;
  permissions: string[];
}

export interface UpdateAdminRequest {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: number[]; // Support multiple roles
  isActive: boolean;
}

export interface AdminRoleAssignmentRequest {
  adminId: string;
  roles: number[]; // Array of role numbers
}

// User Management Types
export interface AdminUserDto {
  id: string;
  email: string;
  phoneNumber?: string;
  firstName: string;
  lastName: string;
  userType: string;
  isActive: boolean;
  isVerified: boolean;
  isEmailVerified: boolean;
  isPhoneVerified: boolean;
  dateJoined: string;
  city?: string;
  province?: string;
  businessName?: string;
  profileImageUrl?: string;
  totalAds: number;
  activeAds: number;
}

export interface AdminUserDetailDto extends AdminUserDto {
  postalCode?: string;
  businessDescription?: string;
  businessWebsite?: string;
  allowPhoneContact: boolean;
  allowEmailContact: boolean;
  receiveNewsletters: boolean;
  receiveMarketingEmails: boolean;
  recentAds: AdminAdSummaryDto[];
}

export interface AdminUpdateUserDto {
  firstName: string;
  lastName: string;
  email: string;
  password?: string;
  phoneNumber?: string;
  isActive: boolean;
  isVerified: boolean;
  isEmailVerified: boolean;
  isPhoneVerified: boolean;
  city?: string;
  province?: string;
  postalCode?: string;
  businessName?: string;
  businessDescription?: string;
  businessWebsite?: string;
  userType: string;
}

export interface AdminCreateUserDto {
  firstName: string;
  lastName: string;
  email: string;
  password?: string;
  phoneNumber?: string;
  isActive: boolean;
  isVerified: boolean;
  isEmailVerified: boolean;
  isPhoneVerified: boolean;
  city?: string;
  province?: string;
  postalCode?: string;
  businessName?: string;
  businessDescription?: string;
  businessWebsite?: string;
  userType: string;
}

// Ad Management Types
export interface AdminAdDto {
  id: string;
  title: string;
  price: number;
  currency?: string;
  status: string;
  datePosted: string;
  dateUpdated?: string;
  expiryDate?: string;
  viewCount: number;
  isFeatured: boolean;
  isUrgent: boolean;
  isPromoted: boolean;
  city: string;
  province: string;
  categoryName: string;
  userName: string;
  userEmail: string;
  userIsVerified: boolean;
}

export interface AdminAdDetailDto extends AdminAdDto {
  description: string;
  isNegotiable: boolean;
  isHighlighted: boolean;
  isTopAd: boolean;
  allowPhoneContact: boolean;
  allowEmailContact: boolean;
  allowChatContact: boolean;
  address: string;
  postalCode: string;
  latitude: number;
  longitude: number;
  userPhone?: string;
  images: AdminAdImageDto[];
  videos: AdminAdVideoDto[];
}

export interface AdminAdImageDto {
  id: string;
  imageUrl: string;
  displayOrder: number;
}

export interface AdminAdVideoDto {
  id: string;
  videoUrl: string;
  thumbnailUrl?: string;
}

export interface AdminAdSummaryDto {
  id: string;
  title: string;
  price: number;
  status: string;
  datePosted: string;
  viewCount: number;
}

export interface AdminUpdateAdDto {
  title: string;
  description: string;
  price: number;
  isNegotiable: boolean;
  status: string;
  isFeatured: boolean;
  isUrgent: boolean;
  isHighlighted: boolean;
  isTopAd: boolean;
  isPromoted: boolean;
}

// Category Management Types
export interface AdminCategoryDto {
  id: string;
  name: string;
  slug: string;
  description?: string;
  iconUrl?: string;
  isActive: boolean;
  sortOrder: number;
  parentCategoryId?: string;
  totalAds: number;
  subCategories: AdminCategoryDto[];
}

export interface AdminCreateCategoryDto {
  name: string;
  slug: string;
  description?: string;
  iconUrl?: string;
  isActive: boolean;
  sortOrder: number;
  parentCategoryId?: string;
}

export interface AdminUpdateCategoryDto {
  name: string;
  slug: string;
  description?: string;
  iconUrl?: string;
  isActive: boolean;
  sortOrder: number;
  parentCategoryId?: string;
}

// Dashboard Types
export interface DashboardOverview {
  users: {
    total: number;
    active: number;
    verified: number;
    thisMonth: number;
    lastMonth: number;
    businessUsers: number;
    individualUsers: number;
  };
  ads: {
    total: number;
    active: number;
    pending: number;
    expired: number;
    rejected: number;
    featured: number;
    promoted: number;
    thisMonth: number;
    lastMonth: number;
    averagePrice: number;
    totalViews: number;
  };
  categories: {
    total: number;
    active: number;
    rootCategories: number;
    subCategories: number;
  };
  admins: {
    total: number;
    active: number;
    superAdmins: number;
    moderators: number;
  };
  growth: {
    userGrowthPercentage: number;
    adGrowthPercentage: number;
  };
}

// Admin API methods
export const adminApi = {
  // Authentication
  login: async (data: AdminLoginRequest): Promise<AxiosResponse<AdminLoginResponse>> => 
    adminApiClient.post('/admin/login', data),
  
  logout: async (): Promise<AxiosResponse<{ message: string }>> => 
    adminApiClient.post('/admin/logout'),
  
  getProfile: async (): Promise<AxiosResponse<AdminUser>> => 
    adminApiClient.get('/admin/profile'),

  // User Management
  getUsers: async (page = 1, pageSize = 50, search?: string): Promise<AxiosResponse<{users: AdminUserDto[], pagination: any}>> => 
    adminApiClient.get(`/admin/users?page=${page}&pageSize=${pageSize}${search ? `&search=${search}` : ''}`),
  
  getUser: async (id: string): Promise<AxiosResponse<AdminUserDetailDto>> => 
    adminApiClient.get(`/admin/users/${id}`),
  
  updateUser: async (id: string, data: AdminUpdateUserDto): Promise<AxiosResponse<{message: string}>> => 
    adminApiClient.put(`/admin/users/${id}`, data),
  
  createUser: async (data: AdminCreateUserDto): Promise<AxiosResponse<{message: string, userId: string}>> => 
    adminApiClient.post('/admin/users', data),
  
  deleteUser: async (id: string): Promise<AxiosResponse<{message: string}>> => 
    adminApiClient.delete(`/admin/users/${id}`),
  
  verifyUser: async (id: string): Promise<AxiosResponse<{message: string}>> => 
    adminApiClient.post(`/admin/users/${id}/verify`),
  
  suspendUser: async (id: string, reason: string): Promise<AxiosResponse<{message: string}>> => 
    adminApiClient.post(`/admin/users/${id}/suspend`, { reason }),
  
  getUserStats: async (): Promise<AxiosResponse<any>> => 
    adminApiClient.get('/admin/users/stats'),

  createUserPlan: async (userId: string, promotionPackageId: string): Promise<AxiosResponse<{message: string, planId: string}>> => 
    adminApiClient.post(`/admin/users/${userId}/plans`, { userId, promotionPackageId }),

  // Ad Management
  getAds: async (page = 1, pageSize = 50, search?: string, status?: string, category?: string): Promise<AxiosResponse<{ads: AdminAdDto[], pagination: any}>> => {
    let url = `/admin/ads?page=${page}&pageSize=${pageSize}`;
    if (search) url += `&search=${search}`;
    if (status) url += `&status=${status}`;
    if (category) url += `&category=${category}`;
    return adminApiClient.get(url);
  },
  
  getAd: async (id: string): Promise<AxiosResponse<AdminAdDetailDto>> => 
    adminApiClient.get(`/admin/ads/${id}`),
  
  updateAd: async (id: string, data: AdminUpdateAdDto): Promise<AxiosResponse<{message: string}>> => 
    adminApiClient.put(`/admin/ads/${id}`, data),
  
  deleteAd: async (id: string): Promise<AxiosResponse<{message: string}>> => 
    adminApiClient.delete(`/admin/ads/${id}`),
  
  approveAd: async (id: string): Promise<AxiosResponse<{message: string}>> => 
    adminApiClient.post(`/admin/ads/${id}/approve`),
  
  rejectAd: async (id: string, reason: string): Promise<AxiosResponse<{message: string}>> => 
    adminApiClient.post(`/admin/ads/${id}/reject`, { reason }),
  
  featureAd: async (id: string, settings: any): Promise<AxiosResponse<{message: string}>> => 
    adminApiClient.post(`/admin/ads/${id}/feature`, settings),
  
  getPendingAds: async (page = 1, pageSize = 20): Promise<AxiosResponse<{ads: AdminAdDto[], pagination: any}>> => 
    adminApiClient.get(`/admin/ads/pending?page=${page}&pageSize=${pageSize}`),
  
  getAdStats: async (): Promise<AxiosResponse<any>> => 
    adminApiClient.get('/admin/ads/stats'),

  // Category Management
  getCategories: async (includeInactive = false): Promise<AxiosResponse<AdminCategoryDto[]>> => 
    adminApiClient.get(`/admin/categories?includeInactive=${includeInactive}`),
  
  getCategory: async (id: string): Promise<AxiosResponse<AdminCategoryDto>> => 
    adminApiClient.get(`/admin/categories/${id}`),
  
  createCategory: async (data: AdminCreateCategoryDto): Promise<AxiosResponse<{message: string, categoryId: string}>> => 
    adminApiClient.post('/admin/categories', data),
  
  updateCategory: async (id: string, data: AdminUpdateCategoryDto): Promise<AxiosResponse<{message: string}>> => 
    adminApiClient.put(`/admin/categories/${id}`, data),
  
  deleteCategory: async (id: string): Promise<AxiosResponse<{message: string}>> => 
    adminApiClient.delete(`/admin/categories/${id}`),
  
  getCategoryStats: async (): Promise<AxiosResponse<any>> => 
    adminApiClient.get('/admin/categories/stats'),

  // Category Attributes Management
  getCategoryAttributes: async (categoryId: string): Promise<AxiosResponse<any>> =>
    adminApiClient.get(`/admin/categories/${categoryId}/attributes`),

  createCategoryAttribute: async (categoryId: string, data: any): Promise<AxiosResponse<any>> =>
    adminApiClient.post(`/admin/categories/${categoryId}/attributes`, data),

  updateCategoryAttribute: async (categoryId: string, attributeId: string, data: any): Promise<AxiosResponse<any>> =>
    adminApiClient.put(`/admin/categories/${categoryId}/attributes/${attributeId}`, data),

  deleteCategoryAttribute: async (categoryId: string, attributeId: string): Promise<AxiosResponse<any>> =>
    adminApiClient.delete(`/admin/categories/${categoryId}/attributes/${attributeId}`),

  // Dashboard
  getDashboardOverview: async (): Promise<AxiosResponse<DashboardOverview>> => 
    adminApiClient.get('/admin/dashboard/overview'),
  
  getRecentActivity: async (): Promise<AxiosResponse<any>> => 
    adminApiClient.get('/admin/dashboard/recent-activity'),
  
  getAnalytics: async (): Promise<AxiosResponse<any>> => 
    adminApiClient.get('/admin/dashboard/analytics'),
  
  getSystemHealth: async (): Promise<AxiosResponse<any>> => 
    adminApiClient.get('/admin/dashboard/system-health'),

  // Admin Management
  createAdmin: async (data: CreateAdminRequest): Promise<AxiosResponse<AdminUser>> => 
    adminApiClient.post('/admin/create', data),
  
  getAllAdmins: async (): Promise<AxiosResponse<AdminUser[]>> => 
    adminApiClient.get('/admin/all'),

  getAdminDetail: async (id: string): Promise<AxiosResponse<AdminDetailDto>> => 
    adminApiClient.get(`/admin/admins/${id}`),

  updateAdmin: async (id: string, data: UpdateAdminRequest): Promise<AxiosResponse<{ message: string }>> => 
    adminApiClient.put(`/admin/admins/${id}`, data),

  deleteAdmin: async (id: string): Promise<AxiosResponse<{ message: string }>> => 
    adminApiClient.delete(`/admin/admins/${id}`),

  toggleAdminActive: async (id: string): Promise<AxiosResponse<{ message: string }>> => 
    adminApiClient.post(`/admin/admins/${id}/toggle-active`),

  assignRoles: async (data: AdminRoleAssignmentRequest): Promise<AxiosResponse<{ message: string }>> => 
    adminApiClient.post('/admin/admins/assign-roles', data),

  resetAdminPassword: async (id: string, newPassword: string): Promise<AxiosResponse<{ message: string }>> => 
    adminApiClient.post(`/admin/admins/${id}/reset-password`, { password: newPassword }),

  getAdminStats: async (): Promise<AxiosResponse<any>> => 
    adminApiClient.get('/admin/admins/stats'),

  // Banner Management
  getBanners: async (): Promise<AxiosResponse<AdminBannerDto[]>> =>
    adminApiClient.get('/admin/banner'),

  getBanner: async (id: string): Promise<AxiosResponse<AdminBannerDto>> =>
    adminApiClient.get(`/admin/banner/${id}`),

  createBanner: async (data: CreateBannerDto): Promise<AxiosResponse<{ message: string; bannerId: string }>> =>
    adminApiClient.post('/admin/banner', data),

  updateBanner: async (id: string, data: UpdateBannerDto): Promise<AxiosResponse<{ message: string }>> =>
    adminApiClient.put(`/admin/banner/${id}`, data),

  deleteBanner: async (id: string): Promise<AxiosResponse<{ message: string }>> =>
    adminApiClient.delete(`/admin/banner/${id}`),

  toggleBannerActive: async (id: string): Promise<AxiosResponse<{ message: string }>> =>
    adminApiClient.post(`/admin/banner/${id}/toggle-active`),

  getBannersByPosition: async (position: number): Promise<AxiosResponse<AdminBannerDto[]>> =>
    adminApiClient.get(`/admin/banner/position/${position}`),

  uploadBannerImage: async (file: File): Promise<AxiosResponse<{ imageUrl: string; message: string }>> => {
    const formData = new FormData();
    formData.append('image', file);
    return adminApiClient.post('/admin/banner/upload-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Content Pages Management
  getContentPages: async (): Promise<AxiosResponse<any[]>> =>
    adminApiClient.get('/admin/content-pages'),

  getContentPage: async (id: string): Promise<AxiosResponse<any>> =>
    adminApiClient.get(`/admin/content-pages/${id}`),

  getContentPageByType: async (pageType: string): Promise<AxiosResponse<any>> =>
    adminApiClient.get(`/admin/content-pages/type/${pageType}`),

  createContentPage: async (data: any): Promise<AxiosResponse<any>> =>
    adminApiClient.post('/admin/content-pages', data),

  updateContentPage: async (id: string, data: any): Promise<AxiosResponse<any>> =>
    adminApiClient.put(`/admin/content-pages/${id}`, data),

  deleteContentPage: async (id: string): Promise<AxiosResponse<{ message: string }>> =>
    adminApiClient.delete(`/admin/content-pages/${id}`),

  // Contact Messages Management
  getContactMessages: async (page: number = 1, pageSize: number = 50): Promise<AxiosResponse<any[]>> =>
    adminApiClient.get(`/admin/contact-messages?page=${page}&pageSize=${pageSize}`),

  getContactMessage: async (id: string): Promise<AxiosResponse<any>> =>
    adminApiClient.get(`/admin/contact-messages/${id}`),

  updateContactMessageStatus: async (id: string, data: { status: number; adminNotes?: string }): Promise<AxiosResponse<any>> =>
    adminApiClient.put(`/admin/contact-messages/${id}/status`, data),

  deleteContactMessage: async (id: string): Promise<AxiosResponse<{ message: string }>> =>
    adminApiClient.delete(`/admin/contact-messages/${id}`),

  getContactMessagesUnreadCount: async (): Promise<AxiosResponse<{ count: number }>> =>
    adminApiClient.get('/admin/contact-messages/unread-count'),
};

// Marketing Codes API
export interface MarketingCodeDto {
  id: string;
  code: string;
  marketingPersonName: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  expiresAt?: string;
  createdByAdminId?: string;
  createdByAdminName?: string;
  totalAdsCount: number;
  activeAdsCount: number;
}

export interface CreateMarketingCodeDto {
  code: string;
  marketingPersonName: string;
  description?: string;
  isActive: boolean;
  expiresAt?: string;
}

export interface UpdateMarketingCodeDto {
  code: string;
  marketingPersonName: string;
  description?: string;
  isActive: boolean;
  expiresAt?: string;
}

export interface MarketingCodeStatsDto {
  id: string;
  code: string;
  marketingPersonName: string;
  totalAdsCount: number;
  activeAdsCount: number;
  pendingAdsCount: number;
  soldAdsCount: number;
  createdAt: string;
  expiresAt?: string;
  isActive: boolean;
}

export const marketingCodesApi = {
  getAll: (): Promise<AxiosResponse<MarketingCodeDto[]>> =>
    adminApiClient.get('/admin/marketing-codes'),

  getById: (id: string): Promise<AxiosResponse<MarketingCodeDto>> =>
    adminApiClient.get(`/admin/marketing-codes/${id}`),

  create: (data: CreateMarketingCodeDto): Promise<AxiosResponse<MarketingCodeDto>> =>
    adminApiClient.post('/admin/marketing-codes', data),

  update: (id: string, data: UpdateMarketingCodeDto): Promise<AxiosResponse<MarketingCodeDto>> =>
    adminApiClient.put(`/admin/marketing-codes/${id}`, data),

  delete: (id: string): Promise<AxiosResponse<void>> =>
    adminApiClient.delete(`/admin/marketing-codes/${id}`),

  toggleStatus: (id: string): Promise<AxiosResponse<void>> =>
    adminApiClient.post(`/admin/marketing-codes/${id}/toggle-status`),

  getStats: (): Promise<AxiosResponse<MarketingCodeStatsDto[]>> =>
    adminApiClient.get('/admin/marketing-codes/stats'),

  getStatsById: (id: string): Promise<AxiosResponse<MarketingCodeStatsDto>> =>
    adminApiClient.get(`/admin/marketing-codes/${id}/stats`),
};

export default adminApi;

// Admin Promotion Packages API
export const adminPromotionPackagesApi = {
  getAll: (): Promise<AxiosResponse<PromotionPackageDto[]>> =>
    adminApiClient.get('/admin/AdminPromotionPackages'),

  getById: (id: string): Promise<AxiosResponse<PromotionPackageDto>> =>
    adminApiClient.get(`/admin/AdminPromotionPackages/${id}`),

  create: (data: CreatePromotionPackageDto): Promise<AxiosResponse<PromotionPackageDto>> =>
    adminApiClient.post('/admin/AdminPromotionPackages', data),

  update: (id: string, data: UpdatePromotionPackageDto): Promise<AxiosResponse<PromotionPackageDto>> =>
    adminApiClient.put(`/admin/AdminPromotionPackages/${id}`, data),

  delete: (id: string): Promise<AxiosResponse<void>> =>
    adminApiClient.delete(`/admin/AdminPromotionPackages/${id}`),

  toggleActive: (id: string): Promise<AxiosResponse<{ message: string }>> =>
    adminApiClient.post(`/admin/AdminPromotionPackages/${id}/toggle-active`),
};

export interface PromotionPackageDto {
  id: string;
  name: string;
  description: string;
  price: number;
  durationDays: number;
  rankingBoost: number;
  maxAdsCount: number;
  
  // Count-based promotional features
  topAdCount: number;
  highlightCount: number;
  urgentTagCount: number;
  bumpUpCount: number;
  homepageGalleryCount: number;
  
  // Keep boolean flags for backward compatibility
  includesTopAd: boolean;
  includesHighlight: boolean;
  includesUrgentTag: boolean;
  includesBumpUp: boolean;
  includesHomepageGallery: boolean;
  isActive: boolean;
  features: string[];
  iconEmoji: string;
  colorClass: string;
  isPopular: boolean;
}

export interface CreatePromotionPackageDto {
  name: string;
  description: string;
  price: number;
  durationDays: number;
  rankingBoost: number;
  maxAdsCount: number;
  
  // Count-based promotional features
  topAdCount: number;
  highlightCount: number;
  urgentTagCount: number;
  bumpUpCount: number;
  homepageGalleryCount: number;
  
  // Keep boolean flags for backward compatibility
  includesTopAd: boolean;
  includesHighlight: boolean;
  includesUrgentTag: boolean;
  includesBumpUp: boolean;
  includesHomepageGallery: boolean;
  isActive: boolean;
}

export interface UpdatePromotionPackageDto {
  name: string;
  description: string;
  price: number;
  durationDays: number;
  rankingBoost: number;
  maxAdsCount: number;
  
  // Count-based promotional features
  topAdCount: number;
  highlightCount: number;
  urgentTagCount: number;
  bumpUpCount: number;
  homepageGalleryCount: number;
  
  // Keep boolean flags for backward compatibility
  includesTopAd: boolean;
  includesHighlight: boolean;
  includesUrgentTag: boolean;
  includesBumpUp: boolean;
  includesHomepageGallery: boolean;
  isActive: boolean;
}

// Banner Management Types
export interface AdminBannerDto {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  linkUrl?: string;
  position: number;
  positionName: string;
  isActive: boolean;
  sortOrder: number;
  startDate?: string;
  endDate?: string;
  createdDate: string;
  updatedDate?: string;
}

export interface CreateBannerDto {
  title: string;
  description: string;
  imageUrl: string;
  linkUrl?: string;
  position: number;
  isActive: boolean;
  sortOrder: number;
  startDate?: string;
  endDate?: string;
}

export interface UpdateBannerDto {
  title: string;
  description: string;
  imageUrl: string;
  linkUrl?: string;
  position: number;
  isActive: boolean;
  sortOrder: number;
  startDate?: string;
  endDate?: string;
} 


// Ad Reports
export interface AdReportDto {
  id: string;
  adId: string;
  adTitle: string;
  adSlug: string;
  reporterId: string;
  reporterName: string;
  reporterEmail: string;
  reporterPhone?: string;
  reporterJoinDate: string;
  reason: number;
  reasonText: string;
  description?: string;
  reportDate: string;
  status: number;
  statusText: string;
  reviewDate?: string;
  reviewedByName?: string;
  adminNotes?: string;
}

export interface UpdateReportStatusDto {
  status: number;
  adminNotes?: string;
}

export const adminReportsApi = {
  getReports: (params: { page: number; pageSize: number; status?: number; reason?: number }) =>
    adminApiClient.get<AdReportDto[]>('/adreport', { params }),

  updateStatus: (id: string, data: UpdateReportStatusDto) =>
    adminApiClient.put(`/adreport/${id}/status`, data),
};


